import React, { useState, useEffect, useCallback } from 'react';
import { getImportaciones, createImportacion, updateImportacion, deleteImportacion } from '../db';

const STATUS_OPTIONS = [
  'Pendiente','En puerto origen','En tránsito',
  'En alta mar','En puerto destino','En aduana','Liberado','Entregado',
];

const STATUS_STYLE = {
  'Pendiente':          { color: '#64748b', bg: '#f1f5f9' },
  'En puerto origen':   { color: '#d97706', bg: '#fef3c7' },
  'En tránsito':        { color: '#2563eb', bg: '#dbeafe' },
  'En alta mar':        { color: '#0284c7', bg: '#e0f2fe' },
  'En puerto destino':  { color: '#7c3aed', bg: '#ede9fe' },
  'En aduana':          { color: '#ea580c', bg: '#ffedd5' },
  'Liberado':           { color: '#059669', bg: '#d1fae5' },
  'Entregado':          { color: '#00b894', bg: '#e6faf6' },
};

const EMPTY = {
  numeroImportacion: '', fechaEtd: '', fechaEta: '',
  bl: '', contenedor: '', nombreBarco: '',
  naviera: '', status: 'Pendiente', posicion: '',
};

function fmtDate(str) {
  if (!str) return '—';
  return new Date(str + 'T12:00:00').toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
}

/* ── Componentes reutilizables ───────────────────────────── */
function Field({ label, name, value, onChange, type = 'text', mono, required }) {
  return (
    <div>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}{required && <span style={{ color: 'var(--red)', marginLeft: 3 }}>*</span>}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        style={{
          width: '100%', padding: '10px 13px',
          background: 'var(--surface)',
          border: '1.5px solid var(--border)',
          borderRadius: 'var(--r-sm)',
          color: 'var(--text)', fontSize: 13,
          fontFamily: mono ? 'var(--mono)' : 'var(--font)',
          outline: 'none', transition: 'border-color 0.18s',
        }}
        onFocus={e => e.target.style.borderColor = 'var(--teal)'}
        onBlur={e => e.target.style.borderColor = 'var(--border)'}
      />
    </div>
  );
}

function Toast({ msg }) {
  if (!msg) return null;
  const ok = msg.type === 'success';
  return (
    <div style={{
      position: 'fixed', top: 76, right: 24, zIndex: 999,
      padding: '12px 20px',
      background: ok ? '#d1fae5' : '#fee2e2',
      border: `1px solid ${ok ? '#6ee7b7' : '#fca5a5'}`,
      color: ok ? '#065f46' : '#991b1b',
      borderRadius: 'var(--r-sm)',
      fontSize: 13, fontWeight: 500,
      boxShadow: 'var(--shadow-lg)',
      animation: 'slideDown 0.25s ease',
    }}>
      <style>{`@keyframes slideDown{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}`}</style>
      {msg.text}
    </div>
  );
}

/* ── Modal de formulario ─────────────────────────────────── */
function ImportForm({ editData, onClose, onSaved }) {
  const [form, setForm] = useState(editData || EMPTY);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const handle = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.numeroImportacion.trim()) { setErr('El número de importación es obligatorio'); return; }
    setSaving(true); setErr('');
    try {
      if (editData?.id) {
        await updateImportacion(editData.id, form);
      } else {
        await createImportacion(form);
      }
      onSaved(editData?.id ? '✅ Importación actualizada' : '✅ Importación creada');
    } catch (e) {
      setErr(e.message || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(13,27,46,0.55)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 200, padding: 24,
    }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '28px 32px',
        width: '100%', maxWidth: 660,
        maxHeight: '90vh', overflowY: 'auto',
        boxShadow: 'var(--shadow-lg)',
        animation: 'slideDown 0.25s ease',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 18, fontWeight: 700 }}>
              {editData?.id ? '✏️ Editar importación' : '➕ Nueva importación'}
            </h2>
            {editData?.id && (
              <div style={{ fontFamily: 'var(--mono)', fontSize: 12, color: 'var(--teal)', marginTop: 3 }}>
                {editData.numeroImportacion}
              </div>
            )}
          </div>
          <button onClick={onClose} style={{
            background: 'var(--surface2)', border: '1px solid var(--border)',
            borderRadius: 8, width: 34, height: 34,
            fontSize: 18, color: 'var(--text-muted)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>×</button>
        </div>

        {err && (
          <div style={{ padding: '10px 14px', background: 'var(--red-lt)', border: '1px solid #fca5a5', borderRadius: 8, color: 'var(--red)', fontSize: 13, marginBottom: 18 }}>
            ⚠️ {err}
          </div>
        )}

        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div style={{ gridColumn: '1/-1' }}>
              <Field label="Número de Importación" name="numeroImportacion" value={form.numeroImportacion} onChange={handle} mono required />
            </div>
            <Field label="Fecha ETD" name="fechaEtd" value={form.fechaEtd} onChange={handle} type="date" />
            <Field label="Fecha ETA" name="fechaEta" value={form.fechaEta} onChange={handle} type="date" />
            <Field label="# BL" name="bl" value={form.bl} onChange={handle} mono />
            <Field label="# Contenedor" name="contenedor" value={form.contenedor} onChange={handle} mono />
            <Field label="Nombre del Barco" name="nombreBarco" value={form.nombreBarco} onChange={handle} />
            <Field label="Naviera" name="naviera" value={form.naviera} onChange={handle} />

            {/* Status */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Status
              </label>
              <select name="status" value={form.status} onChange={handle} style={{
                width: '100%', padding: '10px 13px',
                background: 'var(--surface)',
                border: '1.5px solid var(--border)',
                borderRadius: 'var(--r-sm)',
                color: 'var(--text)', fontSize: 13,
                fontFamily: 'var(--font)', outline: 'none',
              }}>
                {STATUS_OPTIONS.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>

            {/* Posición */}
            <div style={{ gridColumn: '1/-1' }}>
              <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                Posición
              </label>
              <textarea name="posicion" value={form.posicion} onChange={handle} rows={2}
                placeholder="Ej: Océano Pacífico, 15.2°N 120.5°W"
                style={{
                  width: '100%', padding: '10px 13px',
                  background: 'var(--surface)',
                  border: '1.5px solid var(--border)',
                  borderRadius: 'var(--r-sm)',
                  color: 'var(--text)', fontSize: 13,
                  fontFamily: 'var(--font)', outline: 'none',
                  resize: 'vertical',
                }}
                onFocus={e => e.target.style.borderColor = 'var(--teal)'}
                onBlur={e => e.target.style.borderColor = 'var(--border)'}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
            <button type="button" onClick={onClose} style={{
              padding: '10px 20px', background: 'var(--surface2)',
              border: '1px solid var(--border)', borderRadius: 'var(--r-sm)',
              color: 'var(--text)', fontSize: 13,
            }}>Cancelar</button>
            <button type="submit" disabled={saving} style={{
              padding: '10px 24px',
              background: saving ? '#009a7a' : 'var(--teal)',
              color: '#fff', border: 'none',
              borderRadius: 'var(--r-sm)',
              fontWeight: 700, fontSize: 13,
              opacity: saving ? 0.8 : 1,
            }}>{saving ? 'Guardando…' : editData?.id ? 'Actualizar' : 'Crear'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ── Admin Panel principal ───────────────────────────────── */
export default function AdminPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null); // null=closed, {}=new, {...}=edit
  const [delTarget, setDelTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3200);
  };

  const load = useCallback(async () => {
    try {
      setRows(await getImportaciones());
    } catch {
      showToast('error', 'Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSaved = (msg) => {
    setFormData(null);
    showToast('success', msg);
    load();
  };

  const confirmDelete = async () => {
    try {
      await deleteImportacion(delTarget.id);
      setDelTarget(null);
      showToast('success', '🗑️ Importación eliminada');
      load();
    } catch {
      showToast('error', 'Error al eliminar');
    }
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 24px' }}>
      <Toast msg={toast} />

      {/* Modals */}
      {formData !== null && (
        <ImportForm
          editData={formData}
          onClose={() => setFormData(null)}
          onSaved={handleSaved}
        />
      )}

      {delTarget && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(13,27,46,0.55)',
          backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300,
        }}>
          <div style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 16, padding: '32px 36px',
            maxWidth: 380, textAlign: 'center',
            boxShadow: 'var(--shadow-lg)',
          }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>⚠️</div>
            <h3 style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>¿Eliminar importación?</h3>
            <div style={{ fontFamily: 'var(--mono)', fontSize: 14, color: 'var(--teal)', marginBottom: 20 }}>
              {delTarget.numeroImportacion}
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 24 }}>
              Esta acción no se puede deshacer.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
              <button onClick={() => setDelTarget(null)} style={{
                padding: '10px 20px', background: 'var(--surface2)',
                border: '1px solid var(--border)', borderRadius: 8,
                color: 'var(--text)', fontSize: 13,
              }}>Cancelar</button>
              <button onClick={confirmDelete} style={{
                padding: '10px 20px', background: 'var(--red)',
                border: 'none', borderRadius: 8,
                color: '#fff', fontWeight: 700, fontSize: 13,
              }}>Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* Page header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 28, flexWrap: 'wrap', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 4 }}>Panel de Administración</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
            {loading ? 'Cargando…' : `${rows.length} importación${rows.length !== 1 ? 'es' : ''} registrada${rows.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <button onClick={() => setFormData(EMPTY)} style={{
          display: 'flex', alignItems: 'center', gap: 7,
          padding: '10px 20px',
          background: '#232259', color: 'rgb(229 229 229)',
          border: 'none', borderRadius: 'var(--r-sm)',
          fontWeight: 700, fontSize: 13,
          boxShadow: '0 2px 12px rgba(0, 110, 184, 0.3)',
        }}>
          <span style={{ fontSize: 16 }}>+</span> Nueva importación
        </button>
      </div>

      {/* Table */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--r)',
        boxShadow: 'var(--shadow)',
        overflow: 'hidden',
      }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            Cargando desde Firestore…
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 64, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ fontSize: 48, marginBottom: 14 }}>📭</div>
            <p style={{ fontWeight: 500, fontSize: 15, marginBottom: 8 }}>Sin importaciones</p>
            <p style={{ fontSize: 13, marginBottom: 20 }}>Crea la primera para comenzar</p>
            <button onClick={() => setFormData(EMPTY)} style={{
              padding: '9px 20px',
              background: 'transparent',
              border: '1.5px solid var(--teal)',
              borderRadius: 8, color: 'var(--teal)',
              fontSize: 13, fontWeight: 600,
              fontFamily: 'var(--font)',
            }}>+ Crear importación</button>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--surface2)', borderBottom: '1.5px solid var(--border)' }}>
                  {['# Importación','ETD','ETA','BL','Contenedor','Barco','Naviera','Status',''].map(h => (
                    <th key={h} style={{
                      padding: '11px 16px', textAlign: 'left',
                      fontSize: 10, fontWeight: 700,
                      color: 'var(--text-muted)',
                      textTransform: 'uppercase', letterSpacing: 0.6,
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => {
                  const st = STATUS_STYLE[row.status] || { color: '#64748b', bg: '#f1f5f9' };
                  return (
                    <tr key={row.id} style={{
                      borderBottom: i < rows.length - 1 ? '1px solid var(--border)' : 'none',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--surface2)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '13px 16px', fontFamily: 'var(--mono)', fontSize: 12, fontWeight: 500, color: '#0057ff', whiteSpace: 'nowrap' }}>
                        {row.numeroImportacion}
                      </td>
                      <td style={{ padding: '13px 16px', fontSize: 12, whiteSpace: 'nowrap', color: 'var(--text-mid)' }}>{fmtDate(row.fechaEtd)}</td>
                      <td style={{ padding: '13px 16px', fontSize: 12, whiteSpace: 'nowrap', color: 'var(--text-mid)' }}>{fmtDate(row.fechaEta)}</td>
                      <td style={{ padding: '13px 16px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-mid)' }}>{row.bl || '—'}</td>
                      <td style={{ padding: '13px 16px', fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--text-mid)' }}>{row.contenedor || '—'}</td>
                      <td style={{ padding: '13px 16px', fontSize: 12, color: 'var(--text-mid)' }}>{row.nombreBarco || '—'}</td>
                      <td style={{ padding: '13px 16px', fontSize: 12, color: 'var(--text-mid)' }}>{row.naviera || '—'}</td>
                      <td style={{ padding: '13px 16px' }}>
                        <span style={{
                          display: 'inline-block', padding: '4px 10px',
                          borderRadius: 100, fontSize: 11, fontWeight: 600,
                          whiteSpace: 'nowrap',
                          background: st.bg, color: st.color,
                        }}>{row.status}</span>
                      </td>
                      <td style={{ padding: '13px 16px' }}>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button onClick={() => setFormData(row)} style={{
                            padding: '5px 12px', fontSize: 11, fontWeight: 600,
                            background: 'var(--blue-lt)', color: 'var(--blue)',
                            border: '1px solid #bfdbfe', borderRadius: 6,
                            fontFamily: 'var(--font)',
                          }}>Editar</button>
                          <button onClick={() => setDelTarget(row)} style={{
                            padding: '5px 12px', fontSize: 11, fontWeight: 600,
                            background: 'var(--red-lt)', color: 'var(--red)',
                            border: '1px solid #fca5a5', borderRadius: 6,
                            fontFamily: 'var(--font)',
                          }}>Borrar</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
